// frontend/src/widgets/SystemStatusWidget.jsx
// Project:      Vision ANPR by Castle Traffic Ltd - Automatic Number Plate Recognition
// Author:       Leon du Plooy - Castle Traffic Ltd - Copyright 2026
// Contact:      dev@castle-traffic.co.uk
// Created:      2025-05-19
// Updated:      2026-09-09 15:21 BST
//
// Description:
//   Displays system diagnostics in a compact 4-card layout.
//
// Lifecycle Status:
//   Active.
//
// Change Summary (2026-08-27):
//   - Removed automatic system-status fetch on widget mount.
//   - Removed 10-second background polling.
//   - System diagnostics are now retrieved only by explicit user action.
//   - Added manual Load / Refresh Status control.
//   - Added loading and request error states.
//   - Preserved existing CPU, memory, disk, and GPU presentation.
//
// Mobile Responsiveness (2026-09-09):
//   - Preserves the manual-only diagnostics contract: mounting this widget still
//     generates zero /api/system/status requests and no polling is introduced.
//   - Unloaded state remains a simple natural-height panel with a full-width
//     touch-safe Load System Status control on mobile.
//   - Loaded metric cards use the same render tree and remain 4-column on desktop.
//   - Mobile displays one complete metric card per internal vertical scroll/snap.
//   - Dynamic mobile sizing uses the proven Vision pattern:
//       restore previous host sizing;
//       clear temporary viewport/root sizing;
//       measure the natural first metric card;
//       size the card viewport to one complete card;
//       wait a second animation frame;
//       resize the first width-matching fixed-height mobile host.
//   - The footer stacks on mobile and Refresh Status becomes a full-width
//     touch-safe control.
//   - ResizeObserver watches the first loaded card so metric content changes
//     cannot leave the mobile viewport clipped.
//
// Operational Notes:
//   - GET /api/system/status is intentionally expensive because the backend
//     performs synchronous system inspection, including CPU sampling.
//   - This widget must therefore remain user-triggered and must not poll.
//   - Opening a dashboard or mounting this widget must generate zero
//     /api/system/status requests.
//
// Future Intent:
//   - Restrict system diagnostics to appropriate administrative permissions.
//   - Consider a lightweight cached system-health endpoint if live monitoring
//     becomes a genuine operational requirement.

import React, {
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import axios from "axios";

const API_PATH = "/api/system/status";

const getStatusLevel = (value, warning, critical) => {
  const numericValue = Number(value);

  if (!Number.isFinite(numericValue)) {
    return "unknown";
  }

  if (numericValue >= critical) {
    return "critical";
  }

  if (numericValue >= warning) {
    return "warning";
  }

  return "ok";
};

const getColor = (level) => {
  switch (level) {
    case "critical":
      return "#f9dedd";

    case "warning":
      return "#faedad";

    case "ok":
      return "#e0f4ba";

    default:
      return "#f7f7f7";
  }
};

const formatMetricValue = (value, suffix = "") => {
  if (value === null || value === undefined || value === "") {
    return "Unavailable";
  }

  return `${value}${suffix}`;
};

const SystemStatusWidget = () => {
  const widgetRootRef = useRef(null);
  const cardsViewportRef = useRef(null);
  const firstCardRef = useRef(null);

  const [metrics, setMetrics] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const fetchMetrics = useCallback(async () => {
    if (loading) {
      return;
    }

    setLoading(true);
    setError("");

    try {
      const response = await axios.get(API_PATH);

      setMetrics(response.data);
      setLastUpdated(new Date());
    } catch (requestError) {
      console.error(
        "[SystemStatusWidget] Failed to fetch system metrics:",
        requestError
      );

      setError(
        requestError?.response?.data?.detail ||
          requestError?.message ||
          "Unable to retrieve system status."
      );
    } finally {
      setLoading(false);
    }
  }, [loading]);

  const {
    cpu_percent,
    cpu_temp_c,
    memory = {},
    disk = {},
    gpu = {},
  } = metrics || {};

  const cards = metrics
    ? [
        {
          title: "CPU",
          content: [
            `Usage: ${formatMetricValue(cpu_percent, "%")}`,
            cpu_temp_c !== undefined && cpu_temp_c !== null
              ? `Temp: ${formatMetricValue(cpu_temp_c, "°C")}`
              : null,
          ]
            .filter(Boolean)
            .join("\n"),
          level: getStatusLevel(cpu_percent, 70, 90),
        },
        {
          title: "Memory",
          content: [
            `Used: ${formatMetricValue(memory.used_gb, " GB")}`,
            `Total: ${formatMetricValue(memory.total_gb, " GB")}`,
            `Usage: ${formatMetricValue(memory.percent, "%")}`,
          ].join("\n"),
          level: getStatusLevel(memory.percent, 70, 90),
        },
        {
          title: "Disk",
          content: [
            `Used: ${formatMetricValue(disk.used_gb, " GB")}`,
            `Total: ${formatMetricValue(disk.total_gb, " GB")}`,
            `Usage: ${formatMetricValue(disk.percent, "%")}`,
          ].join("\n"),
          level: getStatusLevel(disk.percent, 80, 95),
        },
      ]
    : [];

  if (metrics && gpu?.available) {
    const gpuTemperature =
      gpu.temperature ??
      gpu.temperature_c;

    const gpuLoad =
      gpu.util_gpu ??
      gpu.load;

    const gpuTempLevel = getStatusLevel(
      parseFloat(gpuTemperature),
      75,
      85
    );

    const gpuLoadLevel = getStatusLevel(
      parseFloat(gpuLoad),
      70,
      90
    );

    const overallLevel =
      gpuTempLevel === "critical" ||
      gpuLoadLevel === "critical"
        ? "critical"
        : gpuTempLevel === "warning" ||
          gpuLoadLevel === "warning"
        ? "warning"
        : "ok";

    cards.push({
      title: "GPU",
      content: [
        gpu.name
          ? `Name: ${gpu.name}`
          : null,

        gpuLoad !== undefined &&
        gpuLoad !== null
          ? `Load: ${gpuLoad}`
          : null,

        gpuTemperature !== undefined &&
        gpuTemperature !== null
          ? `Temp: ${gpuTemperature}`
          : null,

        gpu.memory_used !== undefined ||
        gpu.memory_total !== undefined
          ? `Mem: ${gpu.memory_used ?? "?"} / ${gpu.memory_total ?? "?"}`
          : null,
      ]
        .filter(Boolean)
        .join("\n"),
      level: overallLevel,
    });
  }

  useLayoutEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) {
      return undefined;
    }

    const root = widgetRootRef.current;

    if (!root) {
      return undefined;
    }

    const viewport = cardsViewportRef.current;
    const firstCard = firstCardRef.current;
    const mediaQuery = window.matchMedia("(max-width: 767px)");

    let animationFrame = 0;
    let hostRecord = null;

    const restoreHost = () => {
      if (!hostRecord?.node) return;

      hostRecord.node.style.height = hostRecord.height;
      hostRecord.node.style.minHeight = hostRecord.minHeight;
      hostRecord = null;
    };

    const findFixedMobileHost = () => {
      const rootRect = root.getBoundingClientRect();
      const candidates = [];

      let node = root.parentElement;
      let depth = 0;

      while (node && node !== document.body && depth < 6) {
        const rect = node.getBoundingClientRect();
        const inlineHeight = String(node.style?.height || "").trim();

        const widthMatches =
          Math.abs(rect.width - rootRect.width) < 90;

        const hasFixedInlineHeight =
          inlineHeight &&
          inlineHeight !== "auto" &&
          inlineHeight !== "100%" &&
          inlineHeight !== "100vh";

        if (widthMatches && hasFixedInlineHeight) {
          candidates.push(node);
        }

        node = node.parentElement;
        depth += 1;
      }

      return candidates.length ? candidates[0] : null;
    };

    const applyMobileSizing = () => {
      cancelAnimationFrame(animationFrame);
      restoreHost();

      if (viewport) {
        viewport.style.height = "";
        viewport.style.minHeight = "";
      }

      root.style.height = "";
      root.style.minHeight = "";

      if (!mediaQuery.matches) {
        return;
      }

      animationFrame = requestAnimationFrame(() => {
        const currentViewport = cardsViewportRef.current;
        const currentFirstCard = firstCardRef.current;

        if (metrics && currentViewport && currentFirstCard) {
          const cardHeight = Math.ceil(
            Math.max(
              currentFirstCard.getBoundingClientRect().height || 0,
              currentFirstCard.scrollHeight || 0
            )
          );

          if (Number.isFinite(cardHeight) && cardHeight > 0) {
            currentViewport.style.height = `${cardHeight}px`;
            currentViewport.style.minHeight = `${cardHeight}px`;
          }
        }

        root.style.height = "auto";
        root.style.minHeight = "0px";

        animationFrame = requestAnimationFrame(() => {
          const host = findFixedMobileHost();

          if (!host) {
            return;
          }

          hostRecord = {
            node: host,
            height: host.style.height,
            minHeight: host.style.minHeight,
          };

          const hostRect = host.getBoundingClientRect();
          const rootRect = root.getBoundingClientRect();

          const wrapperChromeHeight = Math.max(
            0,
            rootRect.top - hostRect.top
          );

          const requiredHostHeight = Math.ceil(
            Math.max(
              host.scrollHeight,
              wrapperChromeHeight + rootRect.height + 8
            )
          );

          if (
            !Number.isFinite(requiredHostHeight) ||
            requiredHostHeight <= 0
          ) {
            return;
          }

          host.style.height = `${requiredHostHeight}px`;
          host.style.minHeight = `${requiredHostHeight}px`;
        });
      });
    };

    applyMobileSizing();

    const resizeObserver =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(() => applyMobileSizing())
        : null;

    if (resizeObserver && firstCard) {
      resizeObserver.observe(firstCard);
    }

    const handleMediaChange = () => applyMobileSizing();

    mediaQuery.addEventListener?.("change", handleMediaChange);
    window.addEventListener("resize", applyMobileSizing);

    return () => {
      cancelAnimationFrame(animationFrame);
      resizeObserver?.disconnect();

      mediaQuery.removeEventListener?.("change", handleMediaChange);
      window.removeEventListener("resize", applyMobileSizing);

      restoreHost();

      if (viewport) {
        viewport.style.height = "";
        viewport.style.minHeight = "";
      }

      root.style.height = "";
      root.style.minHeight = "";
    };
  }, [
    cards.length,
    error,
    loading,
    metrics,
  ]);

  if (!metrics) {
    return (
      <div
        ref={widgetRootRef}
        className="font-body h-auto min-h-[150px] w-full overflow-visible text-sm md:h-full md:overflow-hidden"
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: "12px",
          padding: "16px",
          textAlign: "center",
          boxSizing: "border-box",
        }}
      >
        <div
          className="break-words"
          style={{
            color: "#666666",
            maxWidth: "420px",
            lineHeight: 1.5,
          }}
        >
          System diagnostics are collected on demand and are not monitored
          automatically.
        </div>

        {error && (
          <div
            role="alert"
            className="break-words"
            style={{
              color: "#b91c1c",
              fontSize: "0.85rem",
              maxWidth: "420px",
              overflowWrap: "anywhere",
            }}
          >
            {error}
          </div>
        )}

        <button
          type="button"
          onClick={fetchMetrics}
          disabled={loading}
          className="min-h-[44px] w-full sm:w-auto"
          style={{
            border: "1px solid #cbd5e1",
            backgroundColor: loading ? "#f1f5f9" : "#ffffff",
            color: loading ? "#64748b" : "#111827",
            borderRadius: "6px",
            padding: "8px 14px",
            fontWeight: 600,
            cursor: loading ? "default" : "pointer",
          }}
        >
          {loading ? "Loading Status..." : "Load System Status"}
        </button>
      </div>
    );
  }

  return (
    <div
      ref={widgetRootRef}
      className="font-body h-auto min-h-0 w-full overflow-visible text-sm md:h-full md:overflow-hidden"
    >
      <style>{`
        @media (max-width: 767px) {
          .vx-system-status-cards {
            overflow-y: auto !important;
            overflow-x: hidden !important;
            overscroll-behavior-y: contain;
            -webkit-overflow-scrolling: touch;
            scrollbar-width: none;
            scroll-snap-type: y mandatory;
          }

          .vx-system-status-cards::-webkit-scrollbar {
            display: none;
          }

          .vx-system-status-card {
            scroll-snap-align: start;
            scroll-snap-stop: always;
          }
        }
      `}</style>

      <div
        ref={cardsViewportRef}
        className="vx-system-status-cards h-auto w-full overflow-visible md:h-auto md:overflow-visible"
      >
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-4">
          {cards.map((card, index) => (
            <div
              key={card.title}
              ref={index === 0 ? firstCardRef : null}
              className="vx-system-status-card min-w-0 rounded p-3 shadow"
              style={{
                backgroundColor: getColor(card.level),
                whiteSpace: "pre-wrap",
                minWidth: 0,
                fontSize: "0.85rem",
                overflowWrap: "anywhere",
              }}
            >
              <strong>{card.title}</strong>

              <div
                style={{
                  marginTop: "0.25rem",
                  overflowWrap: "anywhere",
                }}
              >
                {card.content}
              </div>
            </div>
          ))}
        </div>
      </div>

      {error && (
        <div
          role="alert"
          className="break-words"
          style={{
            color: "#b91c1c",
            fontSize: "0.8rem",
            marginTop: "10px",
            overflowWrap: "anywhere",
          }}
        >
          {error}
        </div>
      )}

      <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between sm:gap-3">
        <div className="break-words text-xs italic text-gray-500">
          Last updated:{" "}
          {lastUpdated
            ? lastUpdated.toLocaleString()
            : "Never"}
        </div>

        <button
          type="button"
          onClick={fetchMetrics}
          disabled={loading}
          className="min-h-[44px] w-full sm:w-auto"
          style={{
            border: "1px solid #cbd5e1",
            backgroundColor: loading ? "#f1f5f9" : "#ffffff",
            color: loading ? "#64748b" : "#111827",
            borderRadius: "6px",
            padding: "7px 12px",
            fontSize: "0.8rem",
            fontWeight: 600,
            cursor: loading ? "default" : "pointer",
          }}
        >
          {loading ? "Refreshing..." : "Refresh Status"}
        </button>
      </div>
    </div>
  );
};

export default SystemStatusWidget;
